import { CalendarDays, Hash, LayoutDashboard, MessageSquare, UserRound } from 'lucide-react';
import { channelLabel, firstSharerName, formatAbsoluteTitle, formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { FirstShare } from '@/types/api';

/**
 * SLC records the FIRST share of a unique link and nothing else — no later sharers, no share
 * count, no channel history. The wording here ("First shared by", "First seen in") is part of
 * the product contract, not decoration. Do not add a count of any kind to this component.
 */
export function FirstShareMeta({
  share,
  className,
  compact = false,
}: {
  share: FirstShare;
  className?: string;
  compact?: boolean;
}) {
  const sharer = firstSharerName(share);
  const channel = channelLabel(share);
  const fromDashboard = share.source === 'dashboard';
  const ChannelIcon = fromDashboard ? LayoutDashboard : Hash;

  return (
    <dl
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground',
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <dt className="flex items-center gap-1.5">
          <UserRound aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
          <span className={compact ? 'sr-only' : ''}>
            {fromDashboard ? 'Added by' : 'First shared by'}
          </span>
        </dt>
        <dd className="min-w-0 truncate font-medium text-foreground">{sharer}</dd>
      </div>

      <div className="flex min-w-0 items-center gap-1.5">
        <dt className="flex items-center gap-1.5">
          <ChannelIcon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
          <span className="sr-only">{fromDashboard ? 'Added on' : 'First seen in'}</span>
        </dt>
        <dd className="min-w-0 truncate">{channel}</dd>
      </div>

      <div className="flex items-center gap-1.5">
        <dt className="flex items-center gap-1.5">
          <CalendarDays aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
          <span className="sr-only">{fromDashboard ? 'Added on' : 'First shared on'}</span>
        </dt>
        <dd>
          <time dateTime={share.sharedAt} title={formatAbsoluteTitle(share.sharedAt)}>
            {formatDate(share.sharedAt)}
          </time>
        </dd>
      </div>
    </dl>
  );
}

/** Link back to the original Slack message, when Slack gave us a permalink. */
export function SlackPermalink({
  share,
  className,
  label = 'View first Slack message',
}: {
  share: FirstShare;
  className?: string;
  label?: string;
}) {
  if (!share.permalink) return null;
  return (
    <a
      href={share.permalink}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md text-xs font-medium text-muted-foreground',
        'transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        className,
      )}
    >
      <MessageSquare aria-hidden="true" className="h-3.5 w-3.5" />
      {label}
    </a>
  );
}
